import { Component } from 'react';
import { connect } from 'react-redux';
import { isEmail } from 'validator';
import api from 'api';
import { breakpoints } from 'styles';
import { Badge } from 'components/common';
import { FormGroup, RadioGroup, Text } from 'components/common/form';

const UsersCreateSingle = connect((state) => ({
  language: state.language,
  messages: state.messages,
  serverConfig: state.serverConfig,
  theme: state.theme
}))(
  class extends Component {
    state = { userSelector: 'useEmail' };

    handleUserSelectorChange = (value) => {
      this.props.onUserSelectorChange(value);
      this.setState({ userSelector: value });
    };

    validateEmail = async (untrimmedValue) => {
      const { formApi, messages } = this.props;

      const value = untrimmedValue?.trim();

      if (!value) return messages.authEmailEmptyError;

      if (!isEmail(value, { domain_specific_validation: true }))
        return messages.usersInvalidEmail;

      try {
        await api.head(`/admin/users?email=${encodeURIComponent(value)}`);
        return messages.authEmailInUseError;
      } catch (err) {
        if (!formApi.getValue('username') && formApi.getError('username'))
          formApi.setFormError('username', null);

        return undefined;
      }
    };

    validateUsername = async (untrimmedValue) => {
      const value = untrimmedValue?.trim();
      if (!value) return undefined;
      if (isEmail(value)) return this.props.messages.authInvalidEmailAsUsername;

      try {
        await api.head(`/admin/users?username=${value}`);
        return this.props.messages.authUsernameInUseError;
      } catch (err) {
        return undefined;
      }
    };

    render() {
      const {
        containerRef,
        formState,
        messages,
        onValueChange,
        serverConfig: {
          accountEnableUsernameAccounts,
          userUseName,
          userUseNameInCertificate
        },
        theme
      } = this.props;

      const { userSelector } = this.state;

      return (
        <>
          {accountEnableUsernameAccounts ? (
            <FormGroup>
              <RadioGroup
                field="userSelector"
                initialValue={userSelector}
                inline={true}
                onChange={this.handleUserSelectorChange}
                options={[
                  {
                    id: 'useEmail',
                    value: 'useEmail',
                    label: messages.userCreateWithEmail
                  },
                  {
                    id: 'useUsername',
                    value: 'useUsername',
                    label: messages.userCreateWithoutEmail
                  }
                ]}
              />
            </FormGroup>
          ) : null}

          <div className="user-info">
            {!accountEnableUsernameAccounts || userSelector === 'useEmail' ? (
              <div className="user-info-email">
                <FormGroup
                  htmlFor="newEmail"
                  label={<span>{messages.commonEmail}</span>}
                  maxWidth="300px"
                >
                  <Text
                    customValidateAsync={this.validateEmail}
                    field="newEmail"
                    id="newEmail"
                    inputContainerRef={containerRef}
                    onChange={onValueChange}
                  />
                  {!formState.errors?.newEmail &&
                  formState.values?.newEmail?.match(/(Å|å|Ä|ä|Ö|ö|Ü|ü)/gm) ? (
                    <Badge
                      category="warning-light"
                      icon="exclamation-triangle"
                      style={{ marginTop: 10 }}
                      text={messages.emailAddressesContainsSpecialCharacters}
                    />
                  ) : null}
                </FormGroup>
              </div>
            ) : null}

            {!userUseName ||
            (accountEnableUsernameAccounts &&
              userSelector === 'useUsername') ? (
              <div className="user-info-username">
                <FormGroup
                  htmlFor="username"
                  label={
                    <span>
                      {messages.commonUsername}
                      {!accountEnableUsernameAccounts ? (
                        <span className="optional">
                          {`(${messages.commonOptional})`}
                        </span>
                      ) : null}
                    </span>
                  }
                  maxWidth="300px"
                >
                  <Text
                    customValidateAsync={this.validateUsername}
                    field="username"
                    id="username"
                    inputContainerRef={containerRef}
                    onChange={onValueChange}
                    style={{
                      marginRight: accountEnableUsernameAccounts ? 20 : 0
                    }}
                  />
                </FormGroup>
              </div>
            ) : null}

            {userUseName || userUseNameInCertificate ? (
              <div className="user-info-names">
                <div className="user-info-first-name">
                  <FormGroup
                    htmlFor="first-name"
                    label={
                      <span>
                        {messages.usersFirstName}
                        <span className="optional">
                          {`(${messages.commonOptional})`}
                        </span>
                      </span>
                    }
                  >
                    <Text field="firstName" id="first-name" />
                  </FormGroup>
                </div>
                <div className="user-info-last-name">
                  <FormGroup
                    htmlFor="last-name"
                    label={
                      <span>
                        {messages.usersLastName}
                        <span className="optional">
                          {`(${messages.commonOptional})`}
                        </span>
                      </span>
                    }
                  >
                    <Text field="lastName" id="last-name" />
                  </FormGroup>
                </div>
              </div>
            ) : null}

            <style jsx>{`
              .user-info {
                align-items: flex-start;
                display: flex;
                flex-wrap: wrap;
                width: 100%;
              }
              .user-info-email,
              .user-info-username,
              .user-info-first-name,
              .user-info-last-name {
                margin-top: 15px;
                max-width: 300px;
                width: 100%;
              }
              .user-info-names {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
              }
              .optional {
                color: ${theme.textColorLighter};
                font-weight: 400;
                margin-left: 5px;
              }
              @media (min-width: ${breakpoints.small}px) {
                .user-info-email {
                  margin-right: 20px;
                }
                .user-info-first-name,
                .user-info-last-name {
                  margin-right: 20px;
                  width: 240px;
                }
              }
            `}</style>
          </div>
        </>
      );
    }
  }
);

export default UsersCreateSingle;
